import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/**
 * RoomContext — Each page declares its atmospheric "room" configuration.
 * AppLayout reads this to configure the aurora color, particle type, etc.
 *
 * Room configs map to the Environmental Rooms plan:
 *   Dashboard  → Command Center (gold aurora, embers)
 *   Reveal     → The Forge (hot gold, intense)
 *   Skills     → The Observatory (deep space, teal accents)
 *   Duels      → The Colosseum (ember, intense)
 *   Hall of Fame → Trophy Room (warm gold, dust motes)
 *   Training   → The Armory (warm gold)
 *   Chat       → The Oracle (dim, cool)
 *   Analysis   → The Scanner (clinical teal)
 */

export interface RoomConfig {
  name: string;
  auroraColor?: string;
  auroraOpacity?: number;
  particleType?: 'embers' | 'dust' | 'none';
  tint?: 'warm' | 'cool' | 'hot' | 'neutral';
}

const ROOM_PRESETS: Record<string, RoomConfig> = {
  'command-center': {
    name: 'Command Center',
    auroraColor: '#E8A020',
    auroraOpacity: 0.15,
    particleType: 'embers',
    tint: 'warm',
  },
  'the-forge': {
    name: 'The Forge',
    auroraColor: '#E8A020',
    auroraOpacity: 0.20,
    particleType: 'embers',
    tint: 'hot',
  },
  'the-observatory': {
    name: 'The Observatory',
    auroraColor: '#1A6B5A',
    auroraOpacity: 0.10,
    particleType: 'dust',
    tint: 'cool',
  },
  'the-colosseum': {
    name: 'The Colosseum',
    auroraColor: '#D4622B',
    auroraOpacity: 0.15,
    particleType: 'embers',
    tint: 'hot',
  },
  'trophy-room': {
    name: 'Trophy Room',
    auroraColor: '#E8A020',
    auroraOpacity: 0.12,
    particleType: 'dust',
    tint: 'warm',
  },
  'the-armory': {
    name: 'The Armory',
    auroraColor: '#E8A020',
    auroraOpacity: 0.12,
    particleType: 'none',
    tint: 'warm',
  },
  'the-oracle': {
    name: 'The Oracle',
    auroraColor: '#1A6B5A',
    auroraOpacity: 0.08,
    particleType: 'none',
    tint: 'cool',
  },
  'the-scanner': {
    name: 'The Scanner',
    auroraColor: '#1A6B5A',
    auroraOpacity: 0.10,
    particleType: 'none',
    tint: 'cool',
  },
};

interface RoomContextType {
  room: RoomConfig;
  setRoom: (preset: string) => void;
  setCustomRoom: (config: RoomConfig) => void;
}

const DEFAULT_ROOM = ROOM_PRESETS['command-center'];

const RoomCtx = createContext<RoomContextType>({
  room: DEFAULT_ROOM,
  setRoom: () => {},
  setCustomRoom: () => {},
});

export function RoomProvider({ children }: { children: ReactNode }) {
  const [room, setRoomState] = useState<RoomConfig>(DEFAULT_ROOM);

  const setRoom = useCallback((preset: string) => {
    setRoomState(ROOM_PRESETS[preset] || DEFAULT_ROOM);
  }, []);

  const setCustomRoom = useCallback((config: RoomConfig) => {
    setRoomState(config);
  }, []);

  return (
    <RoomCtx.Provider value={{ room, setRoom, setCustomRoom }}>
      {children}
    </RoomCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRoom() {
  return useContext(RoomCtx);
}

// eslint-disable-next-line react-refresh/only-export-components
export { ROOM_PRESETS };
